const asDate = (date: Date | string) => {
  const dateObj = new Date(date);
  return dateObj.toLocaleDateString();
}

const asTime = (date: Date | string) => {
  const dateObj = new Date(date);

  // time will be formatted as 12-hour time with AM/PM without seconds

  return dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
}

const asDateTime = (date: Date | string) => {
  // date will be formatted as MM/DD/YYYY
  // time will be formatted as 12-hour time with AM/PM without seconds
  return `${asDate(date)} ${asTime(date)}`;
}

/**
 * Formats a date for use as startDateRange in API calls.
 * Returns date-only string (YYYY-MM-DD) without time or timezone.
 * Uses entity's localTimezone if provided to interpret the date correctly.
 */
const formatStartDateRange = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;
  return formatDateOnly(date, timezone);
}

/**
 * Formats a date for use as endDateRange in API calls.
 * Returns date-only string (YYYY-MM-DD) without time or timezone.
 * Uses entity's localTimezone if provided to interpret the date correctly.
 */
const formatEndDateRange = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;
  return formatDateOnly(date, timezone);
}

/**
 * Formats a date to YYYY-MM-DD string format (for date-only APIs).
 * Uses entity's localTimezone if provided to interpret the date correctly.
 * Always returns date without timezone information.
 */
const formatDateOnly = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;

  // If it's already a YYYY-MM-DD string, return it directly
  if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
    return date;
  }

  // If it's a date string in YYYY-MM-DD format, parse it directly
  if (typeof date === 'string') {
    const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (dateMatch) {
      return `${dateMatch[1]}-${dateMatch[2]}-${dateMatch[3]}`;
    }
  }

  // For Date objects or other formats, interpret in the entity's timezone if provided
  let dateObj: Date;
  if (typeof date === 'string') {
    // If we have a timezone, parse the date string in that timezone
    if (timezone) {
      // Parse the date string and create a date in the specified timezone
      const dateStr = date.split('T')[0]; // Get just the date part
      const [
        year, month, day,
      ] = dateStr.split('-').map(Number);
      if (year && month && day) {
        // Create date string in ISO format and use Intl to get local components
        const tempDate = new Date(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`);
        // Use Intl.DateTimeFormat to get date components in the specified timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });
        const parts = formatter.formatToParts(tempDate);
        const yearPart = parts.find(p => p.type === 'year')?.value;
        const monthPart = parts.find(p => p.type === 'month')?.value;
        const dayPart = parts.find(p => p.type === 'day')?.value;
        if (yearPart && monthPart && dayPart) {
          return `${yearPart}-${monthPart}-${dayPart}`;
        }
      }
    }
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  // If timezone is provided, format the date in that timezone
  if (timezone) {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = formatter.formatToParts(dateObj);
    const year = parts.find(p => p.type === 'year')?.value;
    const month = parts.find(p => p.type === 'month')?.value;
    const day = parts.find(p => p.type === 'day')?.value;
    if (year && month && day) {
      return `${year}-${month}-${day}`;
    }
  }

  // Fallback to local date components (no timezone conversion)
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Formats a datetime to YYYY-MM-DDTHH:mm:ss string format (for datetime APIs).
 * Uses entity's localTimezone if provided to interpret the datetime correctly.
 * Always returns datetime without timezone information.
 */
const formatDateTimeOnly = (date: Date | string | null | undefined, timezone?: string | null): string | null => {
  if (!date) return null;

  let dateObj: Date;
  if (typeof date === 'string') {
    dateObj = new Date(date);
  } else {
    dateObj = date;
  }

  // If timezone is provided, format the datetime in that timezone
  if (timezone) {
    const dateFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const timeFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    const dateParts = dateFormatter.formatToParts(dateObj);
    const timeParts = timeFormatter.formatToParts(dateObj);
    const year = dateParts.find(p => p.type === 'year')?.value;
    const month = dateParts.find(p => p.type === 'month')?.value;
    const day = dateParts.find(p => p.type === 'day')?.value;
    const hour = timeParts.find(p => p.type === 'hour')?.value;
    const minute = timeParts.find(p => p.type === 'minute')?.value;
    const second = timeParts.find(p => p.type === 'second')?.value;
    if (year && month && day && hour && minute && second) {
      return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
    }
  }

  // Fallback to local datetime components (no timezone conversion)
  const year = String(dateObj.getFullYear());
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  const hour = String(dateObj.getHours()).padStart(2, '0');
  const minute = String(dateObj.getMinutes()).padStart(2, '0');
  const second = String(dateObj.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hour}:${minute}:${second}`;
}

export const formatDate = {
  asDate,
  asTime,
  asDateTime,
  formatStartDateRange,
  formatEndDateRange,
  formatDateOnly,
  formatDateTimeOnly,
};
